import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { createWorkOrderComment } from '../api/work-order-commentsAPI';
import { getById } from '../api';
import { parseUtc } from '../utils/dateUtils';

const normalizePhotos = (value) => {
  if (!value) return [];

  const toArray = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input);
        return toArray(parsed);
      } catch (err) {
        return [input];
      }
    }
    if (typeof input === 'object') {
      if (input === null) return [];
      const values = Object.values(input).flat();
      return values.flatMap((entry) => toArray(entry));
    }
    return [];
  };

  return toArray(value)
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === 'string') return entry;
      if (typeof entry === 'object') {
        if (typeof entry.key === 'string') return entry.key;
        if (typeof entry.filename === 'string') return entry.filename;
        if (typeof entry.url === 'string') return entry.url;
      }
      return null;
    })
    .filter((item, index, arr) => item && arr.indexOf(item) === index);
};

const normalizeComment = (comment) => {
  if (!comment) return null;
  const photos = normalizePhotos(comment.photos);
  const createdAt = comment.createdAt || comment.created_at || null;
  const user = comment.user || comment.User || null;

  return {
    ...comment,
    photos,
    createdAt,
    user,
  };
};

const normalizeCommentList = (list) => {
  const array = Array.isArray(list) ? list : [];
  const normalized = array
    .map((item) => normalizeComment(item))
    .filter(Boolean);

  return normalized.sort((a, b) => {
    const getTime = (value) => {
      const raw = value?.createdAt || value?.created_at;
      if (!raw) return 0;
      const date = parseUtc(raw);
      const timestamp = date.getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    };
    return getTime(b) - getTime(a);
  });
};

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = parseUtc(value);
  if (!Number.isFinite(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const getUserImageKey = (user) => {
  if (!user) return null;
  return user.userImage || user.user_image || user.image || null;
};

const getUserInitials = (name) => {
  if (!name) return '';
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const WorkOrderCommentsSection = ({
  workOrderId,
  initialComments = [],
  className = '',
  onCommentActions,
  onCommentsChange,
}) => {
  const userProfile = useUserProfile();
  const userId = userProfile?.id ?? null;
  const normalizedInitialComments = useMemo(
    () => normalizeCommentList(initialComments),
    [initialComments],
  );
  const [formData, setFormData] = useState({ comment: '', photos: [] });
  const [errors, setErrors] = useState({});
  const [comments, setComments] = useState(() => normalizedInitialComments);
  const commentsRef = useRef(normalizedInitialComments);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [commentUserNames, setCommentUserNames] = useState({});

  useEffect(() => {
    setFormData({ comment: '', photos: [] });
    setErrors({});
    setSubmitError('');
  }, [workOrderId]);

  useEffect(() => {
    commentsRef.current = normalizedInitialComments;
    setComments(normalizedInitialComments);
  }, [normalizedInitialComments, workOrderId]);

  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  useEffect(() => {
    let isMounted = true;

    const resolveCommentUserNames = async () => {
      const missingUserIds = Array.from(
        new Set(
          comments
            .map((comment) => {
              const enteredBy = (comment?.enteredBy || '').trim();
              if (enteredBy) return null;
              const existingUserName = (comment?.user?.name || '').trim();
              if (existingUserName) return null;
              return comment?.userId;
            })
            .filter((value) => value !== undefined && value !== null && value !== ''),
        ),
      ).filter((userIdValue) => !commentUserNames[userIdValue]);

      if (missingUserIds.length === 0) return;

      const nextNames = {};
      await Promise.all(
        missingUserIds.map(async (commentUserId) => {
          try {
            const user = await getById('users', commentUserId);
            nextNames[commentUserId] = (user?.name || '').trim() || 'Guest User';
          } catch (error) {
            nextNames[commentUserId] = 'Guest User';
          }
        }),
      );

      if (isMounted && Object.keys(nextNames).length > 0) {
        setCommentUserNames((prev) => ({ ...prev, ...nextNames }));
      }
    };

    resolveCommentUserNames();

    return () => {
      isMounted = false;
    };
  }, [commentUserNames, comments]);

  useEffect(() => {
    if (formData.comment && errors.comment) {
      setErrors((prev) => {
        const { comment: _removed, ...rest } = prev;
        return rest;
      });
    }
  }, [formData.comment, errors.comment]);

  const fields = useMemo(
    () => [
      {
        field: 'comment',
        label: '',
        hideLabel: true,
        type: 'textarea',
        size: 12,
        lines: '4',
        placeholder: 'Add comment',
        required: true,
      },
      {
        field: 'photos',
        label: '',
        type: 'images',
        size: 6,
        directory: 'workorderimages',
      },
    ],
    [],
  );

  const { renderField } = useFormBuilder(fields, formData, setFormData, errors);

  const handleAddComment = useCallback(async () => {
    if (!workOrderId) return false;

    const trimmedComment = (formData.comment || '').trim();
    if (!trimmedComment) {
      setErrors({ comment: 'Comment is required' });
      return false;
    }
    if (!userId) {
      setSubmitError('You must be signed in to add comments.');
      return false;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const numericWorkOrderId = Number(workOrderId);
      const payload = {
        workOrderId: Number.isFinite(numericWorkOrderId) ? numericWorkOrderId : workOrderId,
        userId,
        comments: trimmedComment,
        photos: Array.isArray(formData.photos) ? formData.photos : [],
      };
      const apiResult = await createWorkOrderComment(payload);
      const commentData =
        (apiResult && typeof apiResult === 'object' && 'data' in apiResult
          ? apiResult.data
          : apiResult) || null;
      const baseComment = normalizeComment(commentData) || normalizeComment(payload);
      const hydratedComment = (() => {
        if (!baseComment) return null;
        const fallbackUser =
          baseComment.user ||
          (userProfile
            ? {
                name: userProfile.name || 'You',
                userImage: userProfile.user_image || userProfile.userImage || null,
              }
            : null);
        const fallbackCreatedAt = baseComment.createdAt || new Date().toISOString();
        if (baseComment.id === undefined || baseComment.id === null) {
          return {
            ...baseComment,
            id: Date.now(),
            createdAt: fallbackCreatedAt,
            user: fallbackUser,
          };
        }
        return {
          ...baseComment,
          createdAt: fallbackCreatedAt,
          user: fallbackUser,
        };
      })();

      const existingComments = Array.isArray(commentsRef.current) ? commentsRef.current : [];
      const filteredComments = hydratedComment?.id
        ? existingComments.filter((comment) => comment && comment.id !== hydratedComment.id)
        : existingComments;
      const nextComments = hydratedComment
        ? [hydratedComment, ...filteredComments]
        : filteredComments;

      commentsRef.current = nextComments;
      setComments(nextComments);
      if (typeof onCommentsChange === 'function') {
        onCommentsChange(nextComments);
      }
      setFormData({ comment: '', photos: [] });
      setErrors({});
      return true;
    } catch (err) {
      console.error('Error creating work order comment', err);
      setSubmitError('Unable to save comment. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [formData.comment, formData.photos, onCommentsChange, userId, userProfile, workOrderId]);

  const hasPendingComment = useCallback(() => {
    const trimmedComment = (formData.comment || '').trim();
    return Boolean(trimmedComment);
  }, [formData.comment]);

  const commentActions = useMemo(
    () => ({ submit: handleAddComment, hasPendingComment }),
    [handleAddComment, hasPendingComment],
  );

  useEffect(() => {
    if (typeof onCommentActions !== 'function') return undefined;
    return onCommentActions(commentActions);
  }, [commentActions, onCommentActions]);

  return (
    <div className={`work-order-comments-section ${className}`}>
      <div className="row g-3 align-items-start mt-8">
        {fields.map((field) => renderField(field))}
        <div className="col-md-6 d-flex justify-content-end pt-8">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleAddComment}
            disabled={submitting || !workOrderId}
          >
            <Icon icon="mdi:send-outline" className="icon text-xl line-height-1 me-4" />
            {submitting ? 'Adding...' : 'Post'}
          </button>
        </div>
        {submitError && (
          <div className="col-12">
            <div className="text-danger text-sm">{submitError}</div>
          </div>
        )}
      </div>
      <div className="mt-24">
        {comments.length === 0 ? (
          <div className="text-muted text-sm ms-2">No comments yet.</div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {comments.map((comment) => {
              const enteredByName = (comment?.enteredBy || '').trim();
              const fallbackUserName =
                (comment?.user?.name || '').trim() ||
                commentUserNames[comment?.userId] ||
                'Guest User';
              const userName = enteredByName || fallbackUserName;
              const imageKey = getUserImageKey(comment?.user);
              const userImageUrl =
                imageKey && /^https?:/i.test(imageKey)
                  ? imageKey
                  : imageKey
                    ? `${process.env.REACT_APP_SPACES_BASE}/${imageKey}`
                    : null;
              const initials = getUserInitials(userName);
              const timestamp = comment?.createdAt || comment?.created_at;
              const formattedTimestamp = formatTimestamp(timestamp);
              const photos = Array.isArray(comment?.photos) ? comment.photos : [];

              return (
                <div key={comment.id || `${timestamp}-${userName}`}> 
                  <div className="d-flex gap-3 align-items-start">
                    <div
                      className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
                      style={{ width: '35px', height: '35px', overflow: 'hidden' }}
                    >
                      {userImageUrl ? (
                        <img
                          src={userImageUrl}
                          alt={userName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span className="fw-semibold">{initials}</span>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap justify-content-start align-items-center gap-2">
                        <strong>{userName}</strong>
                        <span className="text-muted text-sm">{formattedTimestamp}</span>
                      </div>
                      <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                        {comment?.comments || comment?.comment || ''}
                      </div>
                      {photos.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-12">
                          {photos.map((photoKey, index) => {
                            const photoUrl =
                              photoKey && /^https?:/i.test(photoKey)
                                ? photoKey
                                : `${process.env.REACT_APP_SPACES_BASE}/${photoKey}`;
                            return (
                                <div
                                  key={`${comment.id || timestamp || index}-${photoKey}-${index}`}
                                  style={{
                                    width: '200px',
                                    height: '200px',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    border: '1px solid #ddd',
                                  }}
                                >
                                  <img
                                    src={photoUrl}
                                    alt={`Comment attachment ${index + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

WorkOrderCommentsSection.propTypes = {
  workOrderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialComments: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  onCommentActions: PropTypes.func,
  onCommentsChange: PropTypes.func,
};

export default WorkOrderCommentsSection;
